/**
 * Structural sponsor-wall data for Bursa Bull Charge × The Edge KL Rat Race 2026.
 *
 * Company names, websites and logo files are FACTS, not translatable copy, so
 * they live here — same rule as src/pages/sponsorship/data.ts. Only headings,
 * tier labels and CTA copy go to i18next (namespace "sponsors").
 *
 * ADDING A SPONSOR = one object literal in the right tier's `sponsors` array.
 * Drop the logo in public/sponsor-logos/<tier>/, kebab-case, lowercase
 * extension, no spaces or parentheses (public/ is copied verbatim by Vite, so
 * the filename IS the URL).
 *
 * FIRST, CROP THE FILE TO ITS INK BOUNDS. This is load-bearing, not tidiness:
 * SponsorWall sizes every logo from its aspect ratio alone (see LOGO_FILL), so
 * baked-in whitespace is indistinguishable from the mark and the logo renders
 * proportionally smaller. Two files have already shipped broken this way — The
 * Edge Malaysia and SD Guthrie both arrived on a 1920x1080 canvas with the mark
 * filling <30% of the height, and a re-exported Mah Sing was 70% whitespace.
 * Keep the uncropped file in ../../../sponsor-source-originals/<tier>/.
 *
 * THEN record the CROPPED file's real pixel dimensions in width/height. A stale
 * pair is silently wrong: it feeds the inline aspect-ratio, so the box shape
 * stops matching the image and object-contain letterboxes the mark.
 */

/**
 * Wall order, top → bottom. `organisers` are the bodies that RUN the event
 * (Bursa × The Edge), not sponsors, so they lead. `officialBank` is a
 * category-exclusive naming right that also sits OUTSIDE the paid ladder; the
 * ladder then reads descending prestige.
 *
 * NOTE this is display order, deliberately the REVERSE of
 * sponsorship/data.ts's `monetaryPackages` (ascending price, flipped at render).
 * Don't "fix" it to match.
 */
export const SPONSOR_TIER_IDS = [
  "organisers",
  "officialBank",
  "platinum",
  "gold",
  "silver",
  "bronze",
  "supporting"
] as const;
export type SponsorTierId = (typeof SPONSOR_TIER_IDS)[number];

export interface Sponsor {
  /** Stable kebab-case id — React key only. */
  id: string;
  /** Brand name. Proper noun → never translated. Doubles as the img alt text. */
  name: string;
  /** Sponsor website, absolute https. Opens in a new tab. */
  href: string;
  /** Site-absolute path under public/sponsor-logos/. */
  logo: string;
  /**
   * Intrinsic pixel size of the CROPPED file. Reserves the box (CLS = 0), feeds
   * the inline aspect-ratio that works around the Safari flex-`img` bug
   * documented in components/layout/Header.tsx, and — because the file is
   * cropped to ink — is also what SponsorWall's equal-area sizing measures.
   */
  width: number;
  height: number;
  /**
   * Escape hatch, defaults to 1. Equal AREA is the right default, but a very
   * dense mark (solid block) can still read heavier than an airy one at the
   * same area. Nudge that one logo (e.g. 0.92) rather than re-tuning the
   * global scale. Reach for this only after cropping — a logo that looks small
   * is almost always an uncropped canvas, not a scale problem.
   */
  opticalScale?: number;
}

export interface SponsorTier {
  id: SponsorTierId;
  sponsors: Sponsor[];
}

/**
 * Within a tier, list alphabetically — no sponsor gets implied precedence.
 *
 * EXCEPT `organisers`, which is a client-specified sequence (see the tier's own
 * note). Don't re-sort it.
 */
export const sponsorTiers: SponsorTier[] = [
  {
    // The organising bodies, not sponsors. Excluded from `allSponsors` (and so
    // from the sponsors ItemList JSON-LD); they're declared as schema.org
    // `organizer` on the event instead — see seo/structuredData.ts, whose
    // `organizer` array mirrors this ORDER and must be kept in step.
    //
    // Order is the client's, not alphabetical: each corporate family runs
    // parent then foundation (Bursa → Yayasan Bursa, The Edge → TEEF).
    id: "organisers",
    sponsors: [
      {
        id: "bursa-malaysia",
        name: "Bursa Malaysia",
        href: "https://www.bursamalaysia.com",
        logo: "/sponsor-logos/organiser/bursa-malaysia.png",
        width: 1149,
        height: 339
      },
      {
        id: "yayasan-bursa-malaysia",
        name: "Yayasan Bursa Malaysia",
        // No standalone domain — YBM lives as a section of the Bursa site.
        href: "https://www.bursamalaysia.com/about_bursa/sustainability/yayasan-bursa-malaysia/overview",
        logo: "/sponsor-logos/organiser/yayasan-bursa-malaysia.png",
        width: 766,
        height: 279
      },
      {
        id: "the-edge-malaysia",
        name: "The Edge Malaysia",
        href: "https://theedgemalaysia.com",
        // Source art sat in a 16:9 canvas with the mark filling only 27% of the
        // height; cropped to its ink bounds so it reads at the same weight as
        // the rest of the wall.
        logo: "/sponsor-logos/organiser/the-edge-malaysia.png",
        width: 900,
        height: 181
      },
      {
        id: "the-edge-education-foundation",
        name: "The Edge Education Foundation",
        href: "https://teef.org.my/",
        logo: "/sponsor-logos/organiser/the-edge-education-foundation.png",
        // Low-res source (276x72) — slightly soft on retina. Drop a larger file
        // at the same path and update these dimensions; nothing else changes.
        width: 276,
        height: 72
      }
    ]
  },
  {
    id: "officialBank",
    sponsors: [
      {
        id: "uob-malaysia",
        name: "UOB Malaysia",
        href: "https://www.uob.com.my/",
        // Carries a hard white matte rather than transparency — see the bg-card
        // note in SponsorWall.tsx for why the tile must stay pure white.
        logo: "/sponsor-logos/official/uob-malaysia.png",
        width: 900,
        height: 256
      }
    ]
  },
  { id: "platinum", sponsors: [] },
  { id: "gold", sponsors: [] },
  {
    id: "silver",
    sponsors: [
      {
        id: "sd-guthrie",
        // The wordmark as it appears in the logo. The registered entity is
        // "SD Guthrie Berhad", but the wall uses trading names throughout
        // (Vistra, UOB Malaysia) — don't expand it.
        name: "SD Guthrie",
        href: "https://www.sdguthrie.com/",
        // Supplied on a 1920x1080 canvas with the mark filling 29% of the
        // height; cropped to its ink bounds like The Edge Malaysia was.
        logo: "/sponsor-logos/silver/sd-guthrie.png",
        width: 1034,
        height: 314
      }
    ]
  },
  {
    id: "bronze",
    sponsors: [
      {
        id: "dcd-technology",
        name: "DCD Technology",
        href: "https://www.dcdtech.com.my/",
        logo: "/sponsor-logos/bronze/dcd-technology.png",
        width: 812,
        height: 379
      },
      {
        id: "mah-sing-group",
        name: "Mah Sing Group",
        href: "https://www.mahsing.com.my/",
        // Re-exported at 1774x887 (70% whitespace) after the first release,
        // which left these dimensions stale and shrank the mark on the wall.
        // Now cropped to ink; both source exports are kept in
        // sponsor-source-originals/bronze/.
        logo: "/sponsor-logos/bronze/mah-sing-group.png",
        width: 1133,
        height: 270
      },
      {
        id: "vistra",
        name: "Vistra",
        href: "https://www.vistra.com/",
        // Widest mark on the wall (5.34:1) — it is what caps LOGO_SCALE in
        // SponsorWall.tsx. A wider logo than this would need that recomputed.
        logo: "/sponsor-logos/bronze/vistra.png",
        width: 769,
        height: 144
      }
    ]
  },
  { id: "supporting", sponsors: [] }
];

/**
 * Tiers that actually have logos — see SHOW_EMPTY_TIER_SLOTS in SponsorWall.tsx.
 * Includes `organisers`. This is what the /sponsors WALL renders; the footer
 * strip renders `stripSponsorTiers` below.
 */
export const filledSponsorTiers = sponsorTiers.filter((tier) => tier.sponsors.length > 0);

/**
 * What the site-wide footer strip renders: sponsors only.
 *
 * Organisers are excluded at CLIENT REQUEST — they run the event rather than
 * buy into it, and they're already credited in full on /sponsors, which is the
 * page that exists for it. Don't "fix" this back to `filledSponsorTiers`.
 */
export const stripSponsorTiers = filledSponsorTiers.filter((tier) => tier.id !== "organisers");

/**
 * Sponsors only — feeds the ItemList JSON-LD so search/AI crawlers can read the
 * names. Organisers are deliberately EXCLUDED: they run the event, so putting
 * them in a list titled "Sponsors" would misstate the relationship.
 */
export const allSponsors = sponsorTiers
  .filter((tier) => tier.id !== "organisers")
  .flatMap((tier) => tier.sponsors);

/** The organising bodies — mirrored into schema.org `organizer` on the event. */
export const allOrganisers =
  sponsorTiers.find((tier) => tier.id === "organisers")?.sponsors ?? [];
